import { ReactElement, useMemo, useCallback, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { getLength } from '@scene'
import numeral from 'numeral'
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import { Stack, Typography, ToggleButton, ToggleButtonGroup, Button } from '@mui/material'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { UnitField } from '@ui/forms'
import { CollapsibleDivider } from '@ui/structure'

type PreservationMode = 'start' | 'end'

const LengthAdjustmentFields = (): ReactElement => {
  const { setValue, formState, reset } = useFormContext()

  // Local state for length adjustment and preservation mode
  const [lengthAdjustment, setLengthAdjustment] = useState<number>(0)
  const [preservationMode, setPreservationMode] = useState<PreservationMode>('start')

  const originalStart = formState.defaultValues?.start as ImmutableVector3
  const originalEnd = formState.defaultValues?.end as ImmutableVector3

  // Function to apply length adjustment based on original geometry
  const handleClickApply = useCallback(() => {
    if (
      !originalStart ||
      !originalEnd ||
      lengthAdjustment === undefined ||
      lengthAdjustment === null
    )
      return

    const dx = originalEnd.x - originalStart.x
    const dy = originalEnd.y - originalStart.y
    const dz = originalEnd.z - originalStart.z
    const originalLength = Math.sqrt(dx * dx + dy * dy + dz * dz)

    if (originalLength === 0) return // Avoid division by zero

    // Calculate the direction vector (unit vector)
    const directionX = dx / originalLength
    const directionY = dy / originalLength
    const directionZ = dz / originalLength

    // Calculate new length
    const newLength = originalLength + lengthAdjustment

    if (newLength <= 0) return // Don't allow negative or zero length

    let newStart: ImmutableVector3
    let newEnd: ImmutableVector3

    if (preservationMode === 'start') {
      // Keep start point, move end point
      newStart = originalStart
      newEnd = new ImmutableVector3(
        originalStart.x + directionX * newLength,
        originalStart.y + directionY * newLength,
        originalStart.z + directionZ * newLength,
      )
    } else {
      // Keep end point, move start point
      newEnd = originalEnd
      newStart = new ImmutableVector3(
        originalEnd.x - directionX * newLength,
        originalEnd.y - directionY * newLength,
        originalEnd.z - directionZ * newLength,
      )
    }

    // Update the form values directly
    setValue('start', newStart)
    setValue('end', newEnd)
  }, [lengthAdjustment, preservationMode, setValue, originalStart, originalEnd])

  // Function to reset to original geometry
  const handleClickReset = useCallback(() => {
    reset()
    setLengthAdjustment(0)
    setPreservationMode('start')
  }, [reset, setLengthAdjustment, setPreservationMode])

  const originalLength = useMemo(() => {
    if (!originalStart || !originalEnd) return 0
    return getLength(originalStart, originalEnd)
  }, [originalStart, originalEnd])

  const newLength = useMemo(() => {
    return originalLength + (lengthAdjustment || 0)
  }, [originalLength, lengthAdjustment])

  return (
    <CollapsibleDivider text="Länge ändern" defaultExpanded={false}>
      <Stack direction="column" spacing={1} alignItems={'center'} sx={{ width: '100%' }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems={'center'}
          justifyContent={'space-between'}
          sx={{ width: '100%' }}
        >
          <Stack direction="row" spacing={1} alignItems={'center'}>
            <Typography>um</Typography>
            <UnitField
              name="lengthAdjustment"
              label=""
              internalUnit="m"
              displayUnit="m"
              placeholder="0.00"
              numeralFormatter="0.00"
              size="small"
              sx={{ width: '100px' }}
              value={lengthAdjustment}
              onChange={event => {
                const value = event.target.value.length ? parseFloat(event.target.value) || 0 : 0
                setLengthAdjustment(value)
              }}
            />
            <Typography>verlängern</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems={'center'}>
            <ToggleButtonGroup
              value={preservationMode}
              exclusive
              onChange={(_, newValue) => {
                if (newValue) {
                  setPreservationMode(newValue as PreservationMode)
                }
              }}
              aria-label="preservation mode"
              fullWidth
              size="small"
            >
              <ToggleButton value="start" aria-label="preserve start" fullWidth>
                Start
              </ToggleButton>
              <ToggleButton value="end" aria-label="preserve end" fullWidth>
                Ende
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography>fixieren</Typography>
          </Stack>
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          alignItems={'center'}
          justifyContent={'space-between'}
          sx={{ width: '100%' }}
        >
          <Stack direction="row" spacing={0} alignItems={'center'}>
            {originalLength > 0 && (
              <Typography noWrap>{numeral(originalLength).format('0.00')} m</Typography>
            )}
            {newLength > 0 && (
              <>
                <ArrowRightAltIcon />
                <Typography noWrap> {numeral(newLength).format('0.00')} m</Typography>
              </>
            )}
          </Stack>

          <Stack direction="row" spacing={1} alignItems={'center'} sx={{ width: '100%' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleClickApply}
              disabled={lengthAdjustment === undefined || lengthAdjustment === null}
              fullWidth
            >
              Anwenden
            </Button>
            <Button size="small" onClick={handleClickReset} fullWidth variant="outlined">
              Zurücksetzen
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </CollapsibleDivider>
  )
}

export default LengthAdjustmentFields
